import { PrimitiveAtom, useAtom } from 'jotai'
import { useState } from 'react'

import {
    chatIdAtom,
    chatSettingsAtom,
    createChat,
    getChatSettings,
    newQuestionAtom,
    partialMessagesAtom,
    postChatSettings,
    saveSettings,
    toggleUploadMobileDrawer,
    updateConversationSettings,
} from '@/store/gigiChat/ChatAtom'
import { deleteTranslateFile, setQuestionActionAtom } from '@/store/translate'
import { IShortMessageObject } from '@/types/gigiChat/Chat'
import { IConversation } from '@/types/gigiChat/Conversation'
import { IConversationSettings } from '@/types/gigiChat/ConversationSettings'
import { deepClone } from '@/utils'

import UploadedFiles from './UploadedFiles'

const Container = () => {
    const [chatSettings] = useAtom<PrimitiveAtom<IConversationSettings | null>>(chatSettingsAtom)

    const [chatId, setChatId] = useAtom<PrimitiveAtom<string>>(chatIdAtom)
    const [, setQuestion] = useAtom<PrimitiveAtom<string | null>>(newQuestionAtom)
    const [itemLoadingDeleteIndex, setItemLoadingDeleteIndex] = useState<number | null>(null)
    const [partialMessage] = useAtom<PrimitiveAtom<Array<IShortMessageObject> | Array<IConversation>>>(partialMessagesAtom)

    const closeMobileDrawer = () => {
        toggleUploadMobileDrawer()
    }

    const onTranslateClick = (localSettings?: IConversationSettings) => {
        const settings = localSettings || chatSettings
        closeMobileDrawer()
        setQuestionActionAtom('QDocTranslate')
        updateConversationSettings(chatId, settings as IConversationSettings, { questionAction: 'QDocTranslate' })
        if (partialMessage[partialMessage.length - 1].question) {
            setChatId('')
            void createChat('Translation').then(() => {
                setQuestion('Translate Documents')
            })
        } else {
            setQuestion('Translate Documents')
        }
    }

    const onItemClick = (selected: boolean, index: number, isTranslate?: boolean) => {
        const localSettings = deepClone<object>(chatSettings || {}) as IConversationSettings
        let docs = localSettings.processingStrategyTranslation.docs

        if (docs) {
            docs = docs.map((item, position) => {
                if (position === index) {
                    item.selected = selected
                } else if (isTranslate) {
                    item.selected = false
                }

                return item
            })

            localSettings.processingStrategyTranslation.docs = docs

            void postChatSettings(chatId, localSettings, true).then((results) => {
                if (results && results.ok) {
                    saveSettings(localSettings)
                    if (isTranslate) {
                        onTranslateClick(localSettings)
                    }
                }
            })
        }
    }

    const onSelectAll = (isSelectAll: boolean) => {
        const localSettings = deepClone<object>(chatSettings || {}) as IConversationSettings
        let docs = localSettings.processingStrategyTranslation.docs

        if (docs) {
            docs = docs.map((item) => {
                item.selected = isSelectAll

                return item
            })

            localSettings.processingStrategyTranslation.docs = docs

            void postChatSettings(chatId, localSettings, true).then((results) => {
                if (results && results.ok) {
                    saveSettings(localSettings)
                }
            })
        }
    }

    const onDelete = (fileName: string, index: number) => {
        setItemLoadingDeleteIndex(index)
        const deleteFileItem = async () => {
            await deleteTranslateFile(fileName).then(async (result) => {
                if (result && result.ok) {
                    const settings = (await getChatSettings(chatId)) as IConversationSettings
                    saveSettings({ ...settings, processingStrategy: 'Translation' })
                }
                setItemLoadingDeleteIndex(null)
            })
        }
        void deleteFileItem()
    }

    if (chatSettings && chatSettings.processingStrategyTranslation.docs.length === 0) {
        return null
    }

    return (
        <UploadedFiles
            files={chatSettings?.processingStrategyTranslation.docs || []}
            onItemClick={onItemClick}
            onSelectAll={onSelectAll}
            onDelete={onDelete}
            loadingDeleteIndex={itemLoadingDeleteIndex}
            onTranslateClick={onTranslateClick}
        />
    )
}

export default Container
